/**
 * V2 formatting helpers — ported from design_reference/js/shell.jsx.
 * Typed equivalents of fmtMoney / fmtK / fmtDate.
 */

/**
 * Formats a number with two decimals and optional leading `$`.
 *
 * @param n - The numeric amount (absolute value is used).
 * @param dollars - When true, prefixes a `$` sign.
 * @returns The formatted money string, e.g. `1,234.50` or `$1,234.50`.
 */
export function fmtMoney(n: number, dollars?: boolean): string {
  const s = Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return (dollars ? '$' : '') + s;
}

/**
 * PRP-MCP-7 G7: the label after a total that leaves out unpriced lines.
 *
 * @param n - How many lines in the total have no price.
 * @returns ` excluding N unpriced line(s)`, or an empty string when none.
 */
export function unpricedNote(n: number): string {
  return n > 0 ? ` excluding ${n} unpriced line${n === 1 ? '' : 's'}` : '';
}

/**
 * Formats a number as a compact `$K` rollup.
 *
 * @param n - The numeric amount.
 * @returns `$X.XK` / `$XXK` for >= 1000, otherwise `$N` rounded.
 */
export function fmtK(n: number): string {
  return n >= 1000
    ? '$' + (n / 1000).toFixed(n >= 10000 ? 0 : 1) + 'K'
    : '$' + Math.round(n);
}

/**
 * Formats an ISO `YYYY-MM-DD` date as `MM/DD/YY`.
 *
 * @param iso - The ISO date string, or null/undefined.
 * @returns The short date, or an em dash when no value is provided.
 */
export function fmtDate(iso?: string | null): string {
  if (!iso) return '—';
  const [y, m, d] = iso.split('-');
  return `${m}/${d}/${y.slice(2)}`;
}
